/**
 * Sprints Page - List and manage sprints
 */
import { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { 
  Plus, Play, CheckCircle, Calendar, Target, 
  TrendingUp, Edit, Trash2
} from 'lucide-react';
import { Sprint } from '@/types';
import { sprintAPI } from '@/api/sprintAPI';
import { sprintStatusAPI, SprintStatus } from '@/api/sprintStatusAPI';
import { SprintModal } from '@/components/SprintModal';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import toast from 'react-hot-toast';
import { DeleteConfirmDialog } from '@/components/DeleteConfirmDialog';
import Share from '@/components/Share';
import { useAppSelector } from '@/store/hooks';
import { projectMemberAPI } from '@/api/projectMemberAPI';

type DialogAction = 'start' | 'complete' | 'delete' | null;

export function Sprints() {
  const { projectId } = useParams<{ projectId: string }>();
  const { user: currentUser } = useAppSelector((state) => state.auth);
  
  const [sprints, setSprints] = useState<Sprint[]>([]);
  const [statuses, setStatuses] = useState<SprintStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingSprint, setEditingSprint] = useState<Sprint | null>(null);
  
  // Confirm dialog state
  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmAction, setConfirmAction] = useState<DialogAction>(null);
  const [selectedSprintId, setSelectedSprintId] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  
  // Project member role state
  const [projectMemberRole, setProjectMemberRole] = useState<string | null>(null);
  const [loadingProjectRole, setLoadingProjectRole] = useState(true);

  // Load current user's project member role
  const loadProjectMemberRole = useCallback(async () => {
    if (!projectId || !currentUser?.id) {
      setLoadingProjectRole(false);
      return;
    }
    
    try {
      setLoadingProjectRole(true);
      const membership = await projectMemberAPI.checkMembership(projectId, currentUser.id);
      setProjectMemberRole(membership.is_member ? (membership.member?.role || null) : null);
    } catch (error) {
      console.error('Failed to load project member role:', error);
      setProjectMemberRole(null);
    } finally {
      setLoadingProjectRole(false);
    }
  }, [projectId, currentUser?.id]);
  
  useEffect(() => {
    if (projectId) {
      loadData();
      loadProjectMemberRole();
    }
  }, [projectId, loadProjectMemberRole]);
  
  // Check if user has sprint admin permissions
  // User has permission if:
  // 1. Main role is 'admin' OR
  // 2. Main role is 'developer' AND project role is 'admin'
  const hasSprintAdminPermission = useMemo((): boolean => {
    if (!currentUser) return false;
    
    const mainRole = currentUser.role?.toLowerCase();
    const projectRole = projectMemberRole?.toLowerCase();
    
    // Main admin role always has permission
    if (mainRole === 'admin' || mainRole === 'super_admin') {
      return true;
    }
    
    // Developer with project admin role has permission
    if (mainRole === 'developer' && projectRole === 'admin') {
      return true;
    }
    
    return false;
  }, [currentUser, projectMemberRole]);

  const loadData = async () => {
    try {
      setLoading(true);
      // Load both sprints and statuses in parallel
      const [sprintsData, statusesData] = await Promise.all([
        sprintAPI.getSprints(projectId),
        sprintStatusAPI.getAll(),
      ]);
      setSprints(sprintsData);
      setStatuses(statusesData);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Handle start sprint with confirmation
  const handleStartSprintClick = (sprintId: string) => {
    setSelectedSprintId(sprintId);
    setConfirmAction('start');
    setShowConfirm(true);
  };

  const handleStartSprintConfirm = async () => {
    if (!selectedSprintId) return;
    setIsProcessing(true);
    try {
      await sprintAPI.startSprint(selectedSprintId);
      toast.success('Sprint started successfully');
      loadData();
      setShowConfirm(false);
      setConfirmAction(null);
      setSelectedSprintId(null);
    } catch (error) {
      toast.error('Failed to start sprint');
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle complete sprint with confirmation
  const handleCompleteSprintClick = (sprintId: string) => {
    setSelectedSprintId(sprintId);
    setConfirmAction('complete');
    setShowConfirm(true);
  };

  const handleCompleteSprintConfirm = async () => {
    if (!selectedSprintId) return;
    setIsProcessing(true);
    try {
      await sprintAPI.completeSprint(selectedSprintId);
      toast.success('Sprint completed successfully');
      loadData();
      setShowConfirm(false);
      setConfirmAction(null);
      setSelectedSprintId(null);
    } catch (error) {
      toast.error('Failed to complete sprint');
    } finally {
      setIsProcessing(false);
    }
  };

  // Handle delete sprint with confirmation
  const handleDeleteSprintClick = (sprintId: string) => {
    setSelectedSprintId(sprintId);
    setConfirmAction('delete');
    setShowConfirm(true);
  };

  const handleDeleteSprintConfirm = async () => {
    if (!selectedSprintId) return;
    setIsProcessing(true);
    try {
      await sprintAPI.deleteSprint(selectedSprintId);
      toast.success('Sprint deleted successfully');
      loadData();
      setShowConfirm(false);
      setConfirmAction(null);
      setSelectedSprintId(null);
    } catch (error) {
      toast.error('Failed to delete sprint');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleConfirmDialogCancel = () => {
    setShowConfirm(false);
    setConfirmAction(null);
    setSelectedSprintId(null);
  };


  const handleEditSprint = (sprint: Sprint) => {
    setEditingSprint(sprint);
    setShowModal(true);
  };

  const getStatusBadge = (status: string) => {
    // Find the status from database
    const statusObj = statuses.find(s => s.name === status);
    
    const colorMap: Record<string, string> = {
      gray: 'bg-gray-100 text-gray-800',
      green: 'bg-green-100 text-green-800',
      blue: 'bg-blue-100 text-blue-800',
      purple: 'bg-purple-100 text-purple-800',
      red: 'bg-red-100 text-red-800',
      yellow: 'bg-yellow-100 text-yellow-800',
      pink: 'bg-pink-100 text-pink-800',
      indigo: 'bg-indigo-100 text-indigo-800',
    };

    const bgColor = statusObj?.color && colorMap[statusObj.color] 
      ? colorMap[statusObj.color]
      : 'bg-gray-100 text-gray-800';

    const displayName = statusObj?.display_name || status.charAt(0).toUpperCase() + status.slice(1);

    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${bgColor}`}>
        {displayName}
      </span>
    );
  };

  // helper removed: getStatusByName

  // Check if sprint status matches a specific status name
  const isSprintStatus = (sprintStatus: string, statusName: string): boolean => {
    return sprintStatus === statusName;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading sprints...</div>
      </div>
    );
  }

  const hasActive = sprints.some(s => s.status === 'active' || (s as any).is_active);

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Sprints</h1>
          <p className="text-gray-600 mt-1">Manage your project sprints</p>
        </div>
        <div className="flex items-center gap-2">
          <Share label="Share" className="btn btn-secondary flex items-center gap-2" subject="Shared view: Sprints" projectId={projectId} />
          {hasSprintAdminPermission && (
            <button
              onClick={() => {
                setEditingSprint(null);
                setShowModal(true);
              }}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              data-testid="create-sprint-button"
            >
              <Plus className="w-4 h-4" />
              Create Sprint
            </button>
          )}
        </div>
      </div>

      {/* Sprints List */}
      {sprints.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No sprints yet</h3>
          <p className="text-gray-600 mb-4">Create your first sprint to get started</p>
          {hasSprintAdminPermission && (
            <button
              onClick={() => setShowModal(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Create Sprint
            </button>
          )}
        </div>
      ) : (
        <div className="grid gap-4">
          {sprints.map((sprint) => (
            <div
              key={sprint.id}
              className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow"
              data-testid={`sprint-card-${sprint.id}`}
            >
              {/* Sprint Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-semibold text-gray-900">
                      {sprint.name}
                    </h3>
                    {getStatusBadge(sprint.status)}
                  </div>
                  {sprint.description && (
                    <p className="text-gray-600 text-sm">{sprint.description}</p>
                  )}
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2">
                  {isSprintStatus(sprint.status, 'planning') && !hasActive && hasSprintAdminPermission && (
                    <button
                      onClick={() => handleStartSprintClick(sprint.id)}
                      className="p-2 text-green-600 hover:bg-green-50 rounded-lg"
                      title="Start Sprint"
                      data-testid={`start-sprint-${sprint.id}`}
                    >
                      <Play className="w-4 h-4" />
                    </button>
                  )}
                  
                  {isSprintStatus(sprint.status, 'active') && hasSprintAdminPermission && (
                    <button
                      onClick={() => handleCompleteSprintClick(sprint.id)}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                      title="Complete Sprint"
                      data-testid={`complete-sprint-${sprint.id}`}
                    >
                      <CheckCircle className="w-4 h-4" />
                    </button>
                  )}
                  
                  {hasSprintAdminPermission && (
                    <button
                      onClick={() => handleEditSprint(sprint)}
                      className="p-2 text-gray-600 hover:bg-gray-50 rounded-lg"
                      title="Edit Sprint"
                      data-testid={`edit-sprint-${sprint.id}`}
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                  )}
                  
                  {!isSprintStatus(sprint.status, 'active') && hasSprintAdminPermission && (
                    <button
                      onClick={() => handleDeleteSprintClick(sprint.id)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                      title="Delete Sprint"
                      data-testid={`delete-sprint-${sprint.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>

              {/* Sprint Goal */}
              {sprint.goal && (
                <div className="flex items-start gap-2 mb-4 p-3 bg-blue-50 rounded-lg">
                  <Target className="w-4 h-4 text-blue-600 mt-0.5" />
                  <div>
                    <div className="text-xs font-medium text-blue-900 mb-1">Sprint Goal</div>
                    <div className="text-sm text-blue-800">{sprint.goal}</div>
                  </div>
                </div>
              )}

              {/* Sprint Dates */}
              <div className="flex items-center gap-6 mb-4 text-sm text-gray-600">
                {sprint.start_date && (
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    <span>
                      {new Date(sprint.start_date).toLocaleDateString()} - 
                      {sprint.end_date ? new Date(sprint.end_date).toLocaleDateString() : 'No end date'}
                    </span>
                  </div>
                )}
                {sprint.capacity && (
                  <div className="flex items-center gap-2">
                    <TrendingUp className="w-4 h-4" />
                    <span>Capacity: {sprint.capacity} SP</span>
                  </div>
                )}
              </div>

              {/* Sprint Metrics */}
              <div className="grid grid-cols-4 gap-4 pt-4 border-t border-gray-200">
                <div>
                  <div className="text-xs text-gray-600 mb-1">Tasks</div>
                  <div className="text-lg font-semibold text-gray-900">
                    {sprint.completed_task_count || 0} / {sprint.task_count || 0}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-gray-600 mb-1">Story Points</div>
                  <div className="text-lg font-semibold text-gray-900">
                    {sprint.completed_story_points} / {sprint.committed_story_points}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-gray-600 mb-1">Progress</div>
                  <div className="text-lg font-semibold text-gray-900">
                    {sprint.progress_percentage?.toFixed(0) || 0}%
                  </div>
                </div>
                <div>
                  <div className="text-xs text-gray-600 mb-1">Velocity</div>
                  <div className="text-lg font-semibold text-gray-900">
                    {sprint.velocity || '-'}
                  </div>
                </div>
              </div>

              {/* Progress Bar */}
              <div className="mt-4">
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-blue-600 h-2 rounded-full transition-all"
                    style={{ width: `${sprint.progress_percentage || 0}%` }}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Sprint Modal */}
      {showModal && projectId && (
        <SprintModal
          projectId={projectId}
          sprint={editingSprint}
          onClose={() => {
            setShowModal(false);
            setEditingSprint(null);
          }}
          onSuccess={loadData}
        />
      )}

      {/* Confirm Dialog */}
      {confirmAction === 'start' && (
        <ConfirmDialog
          isOpen={showConfirm}
          title="Start Sprint"
          message={`Are you sure you want to start sprint "${sprints.find(s => s.id === selectedSprintId)?.name || ''}"?`}
          confirmText="Start"
          icon="warning"
          onConfirm={handleStartSprintConfirm}
          onCancel={handleConfirmDialogCancel}
          isLoading={isProcessing}
        />
      )}
      {confirmAction === 'complete' && (
        <ConfirmDialog
          isOpen={showConfirm}
          title="Complete Sprint"
          message={`Are you sure you want to complete sprint "${sprints.find(s => s.id === selectedSprintId)?.name || ''}"? Incomplete tasks will be moved to backlog.`}
          confirmText="Complete"
          icon="warning"
          onConfirm={handleCompleteSprintConfirm}
          onCancel={handleConfirmDialogCancel}
          isLoading={isProcessing}
        />
      )}
      {confirmAction === 'delete' && (
        <DeleteConfirmDialog
          isOpen={showConfirm}
          itemName={sprints.find(s => s.id === selectedSprintId)?.name || ''}
          itemType="sprint"
          onConfirm={handleDeleteSprintConfirm}
          onCancel={handleConfirmDialogCancel}
          isLoading={isProcessing}
        />
      )}
    </div>
  );
}


